"use client"

import { useRef, useState } from "react"
import { Loader2, Send } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useTranslations } from "@/lib/i18n/client"
import { TurnstileWidget, type TurnstileHandle } from "./TurnstileWidget"

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface NewsletterFormProps {
  domainKey: string
}

export function NewsletterForm({ domainKey }: NewsletterFormProps) {
  const { t } = useTranslations()
  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const turnstileRef = useRef<TurnstileHandle>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const trimmed = email.trim()
    if (!trimmed) {
      setError(t.newsletter.emailRequired)
      return
    }
    if (!EMAIL_REGEX.test(trimmed)) {
      setError(t.newsletter.invalidEmail)
      return
    }
    if (!turnstileToken) {
      setError(t.newsletter.captchaRequired)
      return
    }

    setError(null)
    setLoading(true)

    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed,
          origem: domainKey,
          turnstileToken,
        }),
      })

      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string }
        throw new Error(data.error || t.newsletter.errorMessage)
      }

      toast.success(t.newsletter.successMessage)
      setEmail("")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t.newsletter.errorMessage)
    } finally {
      setLoading(false)
      turnstileRef.current?.reset()
      setTurnstileToken(null)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t.newsletter.emailPlaceholder}
        disabled={loading}
        aria-label={t.newsletter.emailPlaceholder}
        aria-invalid={!!error}
        aria-describedby={error ? "newsletter-error" : undefined}
        className="h-12 border-white/10 bg-white/5 text-sm text-white placeholder:text-slate-400 focus-visible:border-navy-400 focus-visible:ring-0 md:h-10"
      />
      {error && <p id="newsletter-error" role="alert" className="text-xs text-red-300">{error}</p>}
      <TurnstileWidget
        ref={turnstileRef}
        theme="dark"
        size="flexible"
        onVerify={setTurnstileToken}
        onExpire={() => setTurnstileToken(null)}
        onError={() => setTurnstileToken(null)}
      />
      <Button
        type="submit"
        disabled={loading}
        className="h-12 bg-white text-navy-800 hover:bg-slate-100 md:h-10"
      >
        {loading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Send className="mr-2 h-4 w-4" />
        )}
        {t.newsletter.submitButton}
      </Button>
    </form>
  )
}
